import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import {
  detailsPointList,
  projectOutputList,
  curriculumDetailList,
  firstCompanyListPc,
  firstCompanyListM,
  secondCompanyList,
  reviewSlideFirstList,
  reviewSlideSecondList,
  reviewSlideThirdList,
  benefitDetailList,
  benefitSlideList,
  ApplyScheduleList1,
  ApplyScheduleList2,
  EduScheduleList,
  bootCampFaqList,
  bootCampFaqTabList,
} from '../../../../constants/kdt-frontend-14th'
import Link from 'next/link'
import Image from 'next/image'
import ReactPlayer from 'react-player'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'
import CurriculumAccordion from './CurriculumAccordion'
import CurriculumTab from './CurriculumTab'
import { DetailsPointSliderItem, ProjectOutputSliderItem } from './SliderItem'
import styles from './kdt-frontend-14th.module.scss'
import { schoolNavList, ScrollNav } from './ScrollNav'
import Counter from './NumberCounter'
import LazyLoadSection from '@/components/LazyLoadSection'

const FrontKlass14th = () => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [faqTab, setFaqTab] = useState('apply')
  const allFaqs = [
    ...bootCampFaqList.apply,
    ...bootCampFaqList.nebeka,
    ...bootCampFaqList.employment,
    ...bootCampFaqList.edu,
    ...bootCampFaqList.benefit,
    ...bootCampFaqList.etc,
  ]
  const faqList = faqTab === 'all' ? allFaqs : bootCampFaqList[faqTab]
  const onClickFaqTab = (value: string) => {
    setFaqTab(value)
  }
  const earlySessionDate = new Date('2025-04-02 21:00')
  const newSessionDate = new Date('2025-04-16 14:00:01')
  const isAfterEarlySessionDate = useMemo(() => dayjs().isAfter(earlySessionDate), [])
  const [isAfterNewSessionDate, setIsAfterNewSessionDate] = useState(
    dayjs().isAfter(newSessionDate),
  )
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAfterNewSessionDate(dayjs().isAfter(newSessionDate))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const { copyUrlShare } = useCopyUrlShare()

  const [reviewActiveTab, setReviewActiveTab] = useState<
    'reviewFirstList' | 'reviewSecondList' | 'reviewThirdList'
  >('reviewFirstList')

  const [careerActiveTab, setCareerActiveTab] = useState<
    'careerFirstList' | 'careerSecondList' | 'careerThirdList'
  >('careerFirstList')

  const [openedTitle, setOpenedTitle] = useState<string | null>('결과 발표')

  return (
    <div className="relative">
      <section className="bg-[#FFF5EF]">
        {/* Section1_키비주얼 */}
        <div
          data-aos-once="true"
          data-aos="fade-up"
          data-aos-easing="ease"
          data-aos-duration="800"
          data-aos-delay="300"
          className={`${styles.section1_kv} relative w-full`}
        >
          <div className="absolute left-0 top-0 w-full">
            <Image
              src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section1_kv_after_${
                isMobile ? 'm' : 'pc'
              }.png`}
              layout="fill"
              objectFit="cover"
              className="!relative z-10 w-full object-cover"
              alt="[멋쟁이사자처럼] 단번에 프론트엔드 끝내기 - 실무 기반 커리큘럼부터 단독 인턴십까지 단번에 끝내는 프론트엔드 개발자 준비!"
            />
          </div>
        </div>

        {/* Section2_요약정보 */}
        <ul className="mx-auto flex w-full max-w-xl flex-row flex-wrap gap-3 px-4 pb-12 pt-4 lg:max-w-7xl lg:flex-nowrap lg:px-6 lg:pb-20 lg:pt-8">
          <li
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-[calc(50%-6px)] flex-col items-stretch justify-start rounded-lg bg-[#FDFDFD] px-4 py-3 shadow-[0_2px_30px_0_rgba(255,119,16,0.15)] lg:w-auto lg:max-w-[276px] lg:flex-1 lg:gap-1 lg:px-5 lg:py-4"
          >
            <h6 className="text-left text-sm font-bold text-label-normal lg:text-base">
              모집 기간
            </h6>
            {/* Timer */}
            {isAfterNewSessionDate ? (
              <p className="text-left text-sm leading-normal text-label-normal lg:text-base">
                25.03.20(목) 오후 2시 <br />~ 25.04.20(일) 오후 6시
              </p>
            ) : (
              <p className="text-left text-sm leading-normal text-label-normal lg:text-base">
                25.03.20(목) 오후 2시 <br />~ 25.04.16(수) 오후 2시
              </p>
            )}
          </li>
          <li
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="800"
            className="flex w-[calc(50%-6px)] flex-col items-stretch justify-start rounded-lg bg-[#FDFDFD] px-4 py-3 shadow-[0_2px_30px_0_rgba(255,119,16,0.15)] lg:w-auto lg:max-w-[276px] lg:flex-1 lg:gap-1 lg:px-5 lg:py-4"
          >
            <h6 className="text-left text-sm font-bold text-label-normal lg:text-base">
              교육 기간
            </h6>
            <p className="text-left text-sm leading-normal text-label-normal lg:text-base">
              25.04.21(월) <br className="block lg:hidden" />~ 25.10.30.(목)
              <br />
              <span className="text-label-neutral">
                *커리큘럼에 따라 일정이 변경될 수 있습니다.
              </span>
            </p>
          </li>
          <li
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="1000"
            className="flex w-full flex-col items-stretch justify-start rounded-lg bg-[#FDFDFD] px-4 py-3 shadow-[0_2px_30px_0_rgba(255,119,16,0.15)] lg:w-auto lg:max-w-[276px] lg:flex-1 lg:gap-1 lg:px-5 lg:py-4"
          >
            <h6 className="text-left text-sm font-bold text-label-normal lg:text-base">
              교육 비용
            </h6>
            <p className="text-left text-sm leading-normal text-label-normal lg:text-base">
              <span className="line-through">16,698,000</span>원 → 전액 무료
              <br />
              <span className="text-label-neutral">
                매월 최대 81만원의 훈련장려금 신청 가능 <br className="block lg:hidden" />
                (*국민내일배움카드 보유자 한정)
              </span>
            </p>
          </li>
          <li
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="1000"
            className="flex w-full flex-col items-stretch justify-start rounded-lg bg-[#FDFDFD] px-4 py-3 shadow-[0_2px_30px_0_rgba(255,119,16,0.15)] lg:w-auto lg:min-w-[368px] lg:flex-1 lg:gap-1 lg:px-5 lg:py-4"
          >
            <h6 className="text-left text-sm font-bold text-label-normal lg:text-base">
              지원 조건
            </h6>
            <div className={`${styles.section2_intro_list} flex flex-col`}>
              <p className="text-sm text-label-normal lg:text-base">
                국민내일배움카드 보유자 및 발급 가능한 분
              </p>
              <p className="text-sm text-label-normal lg:text-base">
                평일 09:00~18:00 수업에 참여할 수 있는 분
              </p>
              <p className="text-sm text-label-normal lg:text-base">
                직장·학교 병행하지 않고 수업에 집중할 수 있는 분
              </p>
              <p className="text-sm text-label-normal lg:text-base">국내에 거주하고 있는 분</p>
            </div>
          </li>
        </ul>
      </section>

      <ScrollNav schoolNavList={schoolNavList} />

      {/* Section5_멋사단독혜택 */}
      <LazyLoadSection
        className="bg-[#FDFDFD] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        height={960}
        mobileHeight={771}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">멋사 단독 인턴십</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              실무 경험으로 경쟁력 UP!
              <br />
              멋쟁이사자처럼 단독 <br className="block lg:hidden" />
              인턴십 기회 제공
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              글로벌 반도체 기업 OpenSG와 <br className="block lg:hidden" />
              멋쟁이사자처럼 단기 인턴십으로 <br className="hidden lg:block" />
              실무 프로젝트 경험과 <br className="block lg:hidden" />
              취업 경쟁력까지 한 번에 잡으세요.
            </p>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-row items-start gap-4 lg:gap-6"
          >
            <li className="flex w-full flex-col items-stretch justify-center gap-3 lg:gap-5">
              <div className="w-full">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section5_img1_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex w-full flex-col items-start justify-center gap-2 lg:flex-row lg:gap-5">
                <div className="flex w-full flex-row items-center gap-2 lg:w-auto lg:gap-3">
                  <p className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-[rgba(255,119,16,0.10)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                    01
                  </p>
                  <h6 className="text-left text-lg font-bold text-label-normal lg:text-2xl">
                    OpenSG
                  </h6>
                </div>
                <p className="flex-1 text-left text-sm leading-normal text-label-neutral lg:text-base">
                  우수 수료생 한정 <br className="block sm:hidden" />
                  삼성전자, SK하이닉스 등 <br className="block sm:hidden" />
                  글로벌 기업과 협력하는
                  <br />
                  OpenSG의 인턴십 <br className="block sm:hidden" />
                  프로그램에 참여해 실무 <br className="block sm:hidden" />
                  경험을 쌓을 수 있어요.
                </p>
              </div>
            </li>
            <li className="flex w-full flex-col items-stretch justify-center gap-3 lg:gap-5">
              <div className="w-full">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section5_img2_ver2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex w-full flex-col items-start justify-center gap-2 lg:flex-row lg:gap-5">
                <div className="flex w-full flex-row items-center gap-2 lg:w-auto lg:gap-3">
                  <p className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-[rgba(255,119,16,0.10)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                    02
                  </p>
                  <h6 className="text-left text-lg font-bold text-label-normal lg:text-2xl">
                    단기인턴십
                  </h6>
                </div>
                <p className="flex-1 text-left text-sm leading-normal text-label-neutral lg:text-base">
                  4주간 다양한 IT 직무와 협업하며, 내가 만든 프론트엔드 화면이 실제{' '}
                  <br className="hidden lg:block" />
                  서비스로 구현되는 과정을 직접 경험해 보세요!
                </p>
              </div>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section7_상세커리큘럼 */}
      <LazyLoadSection
        height={1161}
        mobileHeight={1309}
        id="curriculum"
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              주차별 상세 커리큘럼
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl lg:leading-tight">
              실무에 강한 개발자로 성장해
              <br />
              취업까지 걱정없는 커리큘럼
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              비전공자도 부담 없이 배울 수 있도록 <br className="block lg:hidden" />
              실무에서 꼭 필요한 스킬들을 단계별로 배우고
              <br />
              내 손으로 직접 웹 사이트를 구축하며 <br className="block lg:hidden" />
              다양한 활용법까지 한 번에 배워보세요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.curriculum_accordion_wrap} hidden flex-row overflow-hidden bg-trueGray-800 lg:flex`}
          >
            <CurriculumTab curriculumDetailList={curriculumDetailList} />
          </div>
        </div>

        <ul
          data-aos-once="true"
          data-aos="fade-up"
          data-aos-easing="ease"
          data-aos-duration="800"
          data-aos-delay="600"
          className={`${styles.curriculum_accordion_wrap} mx-auto flex max-w-xl flex-col overflow-hidden lg:hidden`}
        >
          {curriculumDetailList.map((curriculum, idx) => (
            <CurriculumAccordion contents={curriculum} key={curriculum.title} idx={idx} />
          ))}
        </ul>
      </LazyLoadSection>

      {/* Section8_프로젝트소개 */}
      <LazyLoadSection
        height={1180}
        mobileHeight={1736}
        id="project"
        className="bg-strong pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">프로젝트 소개</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              실무 기반 프로젝트로 완성하는
              <br />
              나만의 포트폴리오
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              프론트엔드 개발자에게 꼭 필요한 ‘협업 능력’
              <br />
              UI → 바닐라 → 파이널 프로젝트까지, 세 가지 스택을
              <br />
              단계별로 경험하며 실무 감각과 팀워크를 키워보세요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col gap-6 lg:flex-row"
          >
            <div className="w-full rounded-lg bg-white p-4 lg:w-auto lg:flex-1 lg:p-6">
              <div className="flex flex-col gap-1 lg:gap-2">
                <h6 className="text-lg font-bold text-label-normal lg:text-2xl">UI 프로젝트</h6>
                <div className="flex flex-row gap-2">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    HTML
                  </p>
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    CSS
                  </p>
                </div>
              </div>
              <div className="my-4 flex flex-row gap-2 lg:my-5 lg:gap-3">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div1_img1.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:max-w-[110px]"
                  alt="-"
                />
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div1_img2.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:max-w-[110px]"
                  alt="-"
                />
              </div>
              <dl className="flex flex-col gap-0.5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">중요성</dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  마크업과 스타일링 기초를 다질 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  반응형, 컴포넌트 기반 UI 구현을 경험해요
                </dd>
              </dl>
              <dl className="mt-4 flex flex-col gap-0.5 border-t pt-4 lg:mt-5 lg:pt-5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">
                  취업에서의 활용
                </dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  퍼블리셔·프론트엔드 기본 역량을 보여주고, 포트폴리오 완성도를 높일 수 있어요
                </dd>
              </dl>
            </div>
            <div className="w-full rounded-lg bg-white p-4 lg:w-auto lg:flex-1 lg:p-6">
              <div className="flex flex-col gap-1 lg:gap-2">
                <h6 className="text-lg font-bold text-label-normal lg:text-2xl">바닐라 프로젝트</h6>
                <div className="flex flex-row gap-2">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    HTML
                  </p>
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    CSS
                  </p>
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    JavaScript
                  </p>
                </div>
              </div>
              <div className="my-4 flex flex-row gap-2 lg:my-5 lg:gap-3">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div1_img1.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div1_img2.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div1_img3.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
              </div>
              <dl className="flex flex-col gap-0.5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">중요성</dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  웹의 기초 개념을 탄탄히 다질 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  웹 접근성을 고려한 개발 경험을 쌓을 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  순수 JavaScript로 DOM 조작과 알고리즘 능력을 키울 수 있어요
                </dd>
              </dl>
              <dl className="mt-4 flex flex-col gap-0.5 border-t pt-4 lg:mt-5 lg:pt-5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">
                  취업에서의 활용
                </dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  기본기가 탄탄한 개발자로 어필할 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  코딩 테스트 및 기술 면접에서 유리해요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  UI/UX 중심 기업에서 강점이 될 수 있어요
                </dd>
              </dl>
            </div>
            <div className="w-full rounded-lg bg-white p-4 lg:w-auto lg:flex-1 lg:p-6">
              <div className="flex flex-col gap-1 lg:gap-2">
                <h6 className="text-lg font-bold text-label-normal lg:text-2xl">파이널 프로젝트</h6>
                <div className="flex flex-row gap-2">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    React
                  </p>
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    TypeScript
                  </p>
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-2 py-1 text-xs font-bold text-yellow-450 lg:px-3 lg:py-[5.5px] lg:text-sm">
                    Next.js
                  </p>
                </div>
              </div>
              <div className="my-4 flex flex-row gap-2 lg:my-5 lg:gap-3">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div2_img1.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div2_img2.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section8_div2_img3.png"
                  width={isMobile ? 93 : 110}
                  height={isMobile ? 93 : 110}
                  className="!lg:w-auto !relative object-cover lg:flex-1"
                  alt="-"
                />
              </div>
              <dl className="flex flex-col gap-0.5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">중요성</dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  최신 기술 스택을 익히며 실무 감각을 키울 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  기획부터 배포까지 전 과정 경험이 가능해요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  타입 안정성을 고려한 유지보수 가능한 코드를 작성할 수 있어요
                </dd>
              </dl>
              <dl className="mt-4 flex flex-col gap-0.5 border-t pt-4 lg:mt-5 lg:pt-5">
                <dt className="text-sm font-bold text-label-normal lg:text-base">
                  취업에서의 활용
                </dt>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  포트폴리오로 활용해 차별화할 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  실무 협업 및 배포 경험을 강조할 수 있어요
                </dd>
                <dd
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-neutral lg:text-base`}
                >
                  면접에서 논리적 사고력과 문제 해결력을 어필할 수 있어요
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section_Add1_멋사 단기 인턴십 */}
      <LazyLoadSection
        height={1124}
        mobileHeight={1250}
        className="bg-[#FDFDFD] pb-16 pt-[88px] lg:pb-[100px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">멋사 단기 인턴십</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              멋쟁이사자처럼 단독
              <br />
              단기 인턴십 제공
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              4주간 다양한 IT 직군과 실무에 가까운 <br className="block lg:hidden" />
              환경에서 협업하며, <br className="hidden lg:block" />
              내가 만든 프론트엔드 화면이 <br className="block lg:hidden" />
              하나의 서비스로 완성되는 과정을 직접 경험해요!
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex aspect-[328/286.59] items-center justify-center overflow-hidden rounded-lg bg-black lg:aspect-[301/263]"
            >
              <div className="w-full">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section_add1_img1.gif"
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full object-cover"
                  alt="-"
                  unoptimized
                />
              </div>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className={`${styles.section_add1_box_wrap_deco_line} flex w-full flex-col gap-3 lg:w-auto lg:flex-1`}
            >
              <div className="flex flex-row items-stretch justify-center gap-3 lg:gap-[30px]">
                <div className="flex h-auto w-[66px] flex-col justify-center lg:w-[102px]">
                  <div className="relative z-[1] flex h-full flex-1 flex-row items-center justify-center">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#FFE3CC] lg:h-3 lg:w-3"></span>
                    <span className="h-[1px] w-3 border border-dashed border-[#FFE3CC]"></span>
                    <div className="flex h-full flex-1 shrink-0 items-center justify-center rounded bg-[#FFE3CC] text-center text-xs font-bold text-label-normal lg:h-auto lg:px-3 lg:py-2 lg:text-sm">
                      STEP <br className="block lg:hidden" />
                      01
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.section_add1_box_wrap_box_deco_arrow} flex h-full flex-1 flex-col gap-0.5 rounded-lg bg-strong p-4 lg:px-6 lg:py-5`}
                >
                  <h6 className="text-sm font-bold text-label-normal lg:text-base">기획 및 설계</h6>
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        요구 사항 정의
                      </p>
                    </li>
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        디자인 시안 및 백엔드 아키텍처 확인
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-row items-stretch justify-center gap-3 lg:gap-[30px]">
                <div className="flex h-auto w-[66px] flex-col justify-center lg:w-[102px]">
                  <div className="relative z-[1] flex h-full flex-1 flex-row items-center justify-center">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#FFCDA5] lg:h-3 lg:w-3"></span>
                    <span className="h-[1px] w-3 border border-dashed border-[#FFCDA5]"></span>
                    <div className="flex h-full flex-1 shrink-0 items-center justify-center rounded bg-[#FFCDA5] text-center text-xs font-bold text-label-normal lg:h-auto lg:py-2 lg:text-sm">
                      STEP <br className="block lg:hidden" />
                      02
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.section_add1_box_wrap_box_deco_arrow} flex h-full flex-1 flex-col gap-0.5 rounded-lg bg-strong p-4 lg:px-6 lg:py-5`}
                >
                  <h6 className="text-sm font-bold text-label-normal lg:text-base">개발 및 구현</h6>
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        최신 프론트엔드 기술 스택을 활용한 <br className="block lg:hidden" />웹
                        페이지 개발
                      </p>
                    </li>
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        API 연동 및 모니터링 기능 개발
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-row items-stretch justify-center gap-3 lg:gap-[30px]">
                <div className="flex h-auto w-[66px] flex-col justify-center lg:w-[102px]">
                  <div className="relative z-[1] flex h-full flex-1 flex-row items-center justify-center">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#FFA256] lg:h-3 lg:w-3"></span>
                    <span className="h-[1px] w-3 border border-dashed border-[#FFA256]"></span>
                    <div className="flex h-full flex-1 shrink-0 items-center justify-center rounded bg-[#FFA256] text-center text-xs font-bold text-label-normal lg:h-auto lg:py-2 lg:text-sm">
                      STEP <br className="block lg:hidden" />
                      03
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.section_add1_box_wrap_box_deco_arrow} flex h-full flex-1 flex-col gap-0.5 rounded-lg bg-strong p-4 lg:px-6 lg:py-5`}
                >
                  <h6 className="text-sm font-bold text-label-normal lg:text-base">배포</h6>
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        디버깅 및 브라우저 테스트
                      </p>
                    </li>
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        실 배포
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-row items-stretch justify-center gap-3 lg:gap-[30px]">
                <div className="flex h-auto w-[66px] flex-col justify-center lg:w-[102px]">
                  <div className="relative z-[1] flex h-full flex-1 flex-row items-center justify-center">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-yellow-450 lg:h-3 lg:w-3"></span>
                    <span className="h-[1px] w-3 border border-dashed border-yellow-450"></span>
                    <div className="flex h-full flex-1 shrink-0 items-center justify-center rounded bg-yellow-450 text-center text-xs font-bold text-label-normal lg:h-auto lg:py-2 lg:text-sm">
                      STEP <br className="block lg:hidden" />
                      04
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.section_add1_box_wrap_box_deco_arrow} flex h-full flex-1 flex-col gap-0.5 rounded-lg bg-strong p-4 lg:px-6 lg:py-5`}
                >
                  <h6 className="text-sm font-bold text-label-normal lg:text-base">
                    프로젝트 완료
                  </h6>
                  <ul className="flex flex-col gap-0.5">
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        이력서, 자기소개서, 포트폴리오 피드백
                      </p>
                    </li>
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        수료증 제공
                      </p>
                    </li>
                    <li>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_add1} text-sm text-label-normal lg:text-base`}
                      >
                        (우수 수료자 한정) 추천서 제공
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-10 text-center text-sm leading-normal text-label-neutral lg:mt-16 lg:text-base">
            *위 이미지는 실제 단기 인턴십 참가자 산출물로 해당 인턴십은 우수 수료생 한정으로
            운영되며, <br className="block lg:hidden" />
            실무 과제/환경 변동에 따라 변경될 수 있습니다.
          </p>
        </div>
      </LazyLoadSection>

      {/* Section9_프로젝트결과물 */}
      <LazyLoadSection
        className="bg-[#FDFDFD] pb-[104px] pt-[88px] lg:pb-[290px] lg:pt-36"
        height={1387}
        mobileHeight={611}
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              수료생 프로젝트 결과물
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              2025 취업의 핵심은
              <br />
              실무 경험을 담은 포트폴리오
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              실제 수료생들의 프로젝트 결과물을 보고,
              <br />
              기업이 원하는 포트폴리오 기준을 확인해 보세요
            </p>
          </div>
        </div>

        <div
          data-aos-once="true"
          data-aos="fade-up"
          data-aos-easing="ease"
          data-aos-duration="800"
          data-aos-delay="600"
          className={`${styles.section9_slider}`}
        >
          <ProjectOutputSliderItem contents={projectOutputList} />
        </div>
      </LazyLoadSection>

      {/* Section_Add2_생성형 AI 가이드 */}
      <LazyLoadSection
        height={919}
        mobileHeight={868}
        className="bg-strong pb-16 pt-[88px] lg:pb-[100px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">생성형 AI 가이드</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              개발 효율 200% UP!
              <br />
              생성형 AI 실전 활용 가이드
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              생성형 AI, 단순 도구로 쓰기엔 아깝지 않았나요?
              <br />
              문제 해결력을 키워줄 ‘개발 파트너’로 제대로 활용해 보세요
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row lg:gap-[38px]">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="aspect-[82/47] w-full overflow-hidden rounded-lg lg:aspect-[709/409] lg:w-auto lg:max-w-[709px]"
            >
              <Image
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section_add2_img1.gif"
                layout="fill"
                objectFit="cover"
                className="!relative w-full object-cover"
                alt="-"
                unoptimized
              />
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1"
            >
              <div
                className={`${styles.section_add2_box_wrap_box_deco_arrow} flex h-auto flex-1 flex-row items-center justify-start gap-3 rounded-lg bg-white p-3 lg:px-5 lg:py-5`}
              >
                <p className="flex h-7 w-7 items-center justify-center rounded bg-[rgba(255,119,16,0.15)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                  01
                </p>
                <h6 className="text-sm text-label-normal lg:text-base">
                  마크업과 생성형 AI로 익히는 개발 기초 실습
                </h6>
              </div>
              <div
                className={`${styles.section_add2_box_wrap_box_deco_arrow} flex h-auto flex-1 flex-row items-center justify-start gap-3 rounded-lg bg-white p-3 lg:px-5 lg:py-5`}
              >
                <p className="flex h-7 w-7 items-center justify-center rounded bg-[rgba(255,119,16,0.15)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                  02
                </p>
                <h6 className="text-sm text-label-normal lg:text-base">
                  생성형 AI 코드 오류 분석을 통한 사고력 훈련
                </h6>
              </div>
              <div
                className={`${styles.section_add2_box_wrap_box_deco_arrow} flex h-auto flex-1 flex-row items-center justify-start gap-3 rounded-lg bg-white p-3 lg:px-5 lg:py-5`}
              >
                <p className="flex h-7 w-7 items-center justify-center rounded bg-[rgba(255,119,16,0.15)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                  03
                </p>
                <h6 className="text-sm text-label-normal lg:text-base">
                  좋은 질문으로 AI 답변 퀄리티 높이는 방법
                </h6>
              </div>
              <div
                className={`${styles.section_add2_box_wrap_box_deco_arrow} flex h-auto flex-1 flex-row items-center justify-start gap-3 rounded-lg bg-white p-3 lg:px-5 lg:py-5`}
              >
                <p className="flex h-7 w-7 items-center justify-center rounded bg-[rgba(255,119,16,0.15)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                  04
                </p>
                <h6 className="text-sm text-label-normal lg:text-base">
                  개발적 사고로 효율적인 해결책을 도출하는 역량
                </h6>
              </div>
              <div
                className={`${styles.section_add2_box_wrap_box_deco_arrow} flex h-auto flex-1 flex-row items-center justify-start gap-3 rounded-lg bg-white p-3 lg:px-5 lg:py-5`}
              >
                <p className="flex h-7 w-7 items-center justify-center rounded bg-[rgba(255,119,16,0.15)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                  05
                </p>
                <h6 className="text-sm text-label-normal lg:text-base">
                  코드 품질과 생산성을 높이는 효율적 개발 능력
                </h6>
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section10_취업결과 */}
      <LazyLoadSection
        className={`${styles.section10_bg} pb-16 pt-[88px] lg:pb-[100px] lg:pt-36`}
        height={778}
        mobileHeight={815}
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">수강생 취업 결과</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              수료생 합격 기업
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              수강생 만족도 ★4.5, 14기째 이어온 검증된 커리큘럼!
              <br />
              많은 수료생이 취업에 성공한 것처럼,
              <br />
              지금 합류해 개발 실력을 높여보세요
            </p>
          </div>
        </div>

        <div className="mb-10 flex flex-col items-center justify-center lg:mb-16 lg:flex-row">
          <div className="mb-4 w-[284px] border-b pb-4 lg:mb-0 lg:mr-8 lg:w-auto lg:border-b-0 lg:border-r lg:pb-0 lg:pr-8">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className={`${styles.section10_div_deco} mx-auto flex h-[98px] w-[215px] flex-col items-center justify-center lg:h-[143px] lg:w-[274px] lg:justify-start lg:gap-1`}
            >
              <p className="font-bold text-label-normal lg:text-lg">수료율</p>
              <h6 className="text-3xl font-bold text-yellow-450 lg:text-5xl">
                <Counter start={0} end="100" duration={1100} />%
              </h6>
              <p className="text-sm leading-normal text-label-neutral lg:text-base">
                3기, 5기, 7기, 8기
              </p>
            </div>
          </div>
          <div className="mb-4 w-[284px] border-b pb-4 lg:mb-0 lg:mr-8 lg:w-auto lg:border-b-0 lg:border-r lg:pb-0 lg:pr-8">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className={`${styles.section10_div_deco} mx-auto flex h-[101px] w-[261px] flex-col items-center justify-center lg:h-[143px] lg:w-[377px] lg:justify-start lg:gap-1`}
            >
              <p className="font-bold text-label-normal lg:text-lg">고용 24 만족도</p>
              <h6 className="text-3xl font-bold text-yellow-450 lg:text-5xl">
                <Counter start={0} end="4.54" duration={1300} />
                /5.00
              </h6>
              <p className="text-center text-sm leading-normal text-label-neutral lg:text-base">
                멋쟁이사자처럼 프론트엔드
                <br />
                1기부터 11기까지 평균
              </p>
            </div>
          </div>
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="1000"
            className={`${styles.section10_div_deco} flex h-[98px] w-[192px] flex-col items-center justify-center lg:h-[143px] lg:w-[250px] lg:justify-start lg:gap-1`}
          >
            <p className="font-bold text-label-normal lg:text-lg">누적 교육 기수</p>
            <h6 className="text-3xl font-bold text-yellow-450 lg:text-5xl">
              <Counter start={0} end="13" duration={1700} />기
            </h6>
          </div>
        </div>

        <div className="flex flex-col overflow-hidden">
          <div className={`${styles.company_logo_flow_slider}`}>
            <div className={`${styles.company_logo_flow_slider_track}`}>
              {[
                ...(isMobile ? firstCompanyListM : firstCompanyListPc),
                ...(isMobile ? firstCompanyListM : firstCompanyListPc),
                ...(isMobile ? firstCompanyListM : firstCompanyListPc),
              ].map((item, idx) => (
                <div key={`${item}_${idx}`} className={`${styles.slider_item}`}>
                  <img className="h-full w-full object-contain" src={item} alt="company_logo" />
                </div>
              ))}
            </div>
          </div>

          <div
            className={`${styles.company_logo_flow_slider} ${styles.company_logo_flow_slider_second} lg:hidden`}
          >
            <div className={`${styles.company_logo_flow_slider_track}`}>
              {[...secondCompanyList, ...secondCompanyList, ...secondCompanyList].map(
                (item, idx) => (
                  <div key={`${item}_${idx}`} className={`${styles.slider_item}`}>
                    <img className="h-full w-full object-contain" src={item} alt="company_logo" />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section11_수강후기 */}
      <LazyLoadSection
        className="bg-[#FDFDFD] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        height={817}
        mobileHeight={1178}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">수강 후기</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-label-normal lg:mb-5 lg:text-4xl lg:leading-tight">
              앞서 졸업한 수료생들의 <br className="block lg:hidden" />
              생생한 후기
            </h4>
            <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
              수료생이 직접 경험한 생생한 후기를 만나보세요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col gap-6 lg:flex-row lg:gap-10"
          >
            <div className="flex shrink-0 flex-row items-center justify-center gap-1 lg:flex-col lg:items-start lg:justify-start lg:gap-4">
              {[
                { key: 'reviewFirstList', label: '프로젝트' },
                { key: 'reviewSecondList', label: '커리큘럼' },
                { key: 'reviewThirdList', label: '강사/멘토/운영진' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() =>
                    setReviewActiveTab(
                      key as 'reviewFirstList' | 'reviewSecondList' | 'reviewThirdList',
                    )
                  }
                  className={`max-w-fit rounded-full border px-5 py-1.5 text-sm transition-all lg:px-6 lg:py-2 lg:text-lg ${
                    reviewActiveTab === key
                      ? 'border-yellow-450 bg-yellow-450 font-bold text-white'
                      : 'border-label-assistive bg-white font-normal text-label-neutral'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="w-full lg:w-[calc(100%-209px)] lg:max-w-[1023px]">
              {reviewActiveTab === 'reviewFirstList' && (
                <ul className="mx-auto flex w-full flex-col gap-4 lg:flex-row">
                  {reviewSlideFirstList.map(review => (
                    <li
                      className="flex flex-col gap-4 overflow-hidden rounded-lg bg-strong p-4 lg:h-[300px] lg:flex-1"
                      key={review.title}
                    >
                      <div className="flex flex-col border-b pb-4 lg:gap-0.5">
                        <img
                          className="h-5 w-5 object-cover"
                          src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-dataanalysis-5th/section11_deco.png"
                          alt="-"
                        />
                        <h6 className="whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                          {review.title}
                        </h6>
                      </div>
                      <div className="lg:flex-1">
                        <p className="text-base leading-normal text-label-neutral">{review.desc}</p>
                      </div>
                      <p className="w-full text-right text-base text-label-alternative">
                        {review.who}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {reviewActiveTab === 'reviewSecondList' && (
                <ul className="mx-auto flex w-full flex-col gap-4 lg:flex-row">
                  {reviewSlideSecondList.map(review => (
                    <li
                      className="flex flex-col gap-4 overflow-hidden rounded-lg bg-strong p-4 lg:h-[300px] lg:flex-1"
                      key={review.title}
                    >
                      <div className="flex flex-col border-b pb-4 lg:gap-0.5">
                        <img
                          className="h-5 w-5 object-cover"
                          src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-dataanalysis-5th/section11_deco.png"
                          alt="-"
                        />
                        <h6 className="whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                          {review.title}
                        </h6>
                      </div>
                      <div className="lg:flex-1">
                        <p className="text-base leading-normal text-label-neutral">{review.desc}</p>
                      </div>
                      <p className="w-full text-right text-base text-label-alternative">
                        {review.who}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {reviewActiveTab === 'reviewThirdList' && (
                <ul className="mx-auto flex w-full flex-col gap-4 lg:flex-row">
                  {reviewSlideThirdList.map(review => (
                    <li
                      className="flex flex-col gap-4 overflow-hidden rounded-lg bg-strong p-4 lg:h-[300px] lg:flex-1"
                      key={review.title}
                    >
                      <div className="flex flex-col border-b pb-4 lg:gap-0.5">
                        <img
                          className="h-5 w-5 object-cover"
                          src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-dataanalysis-5th/section11_deco.png"
                          alt="-"
                        />
                        <h6 className="whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                          {review.title}
                        </h6>
                      </div>
                      <div className="lg:flex-1">
                        <p className="text-base leading-normal text-label-neutral">{review.desc}</p>
                      </div>
                      <p className="w-full text-right text-base text-label-alternative">
                        {review.who}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section12_개발자고민 */}
      <LazyLoadSection
        className="bg-[#FFF5EF] pb-24 pt-20 lg:py-40"
        height={728}
        mobileHeight={715}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:flex lg:max-w-7xl lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="lg:w-1/2 lg:min-w-[462px] lg:max-w-[499px]"
          >
            <div className="flex w-full flex-col items-center justify-center lg:items-start">
              <h4 className="mb-10 text-center text-2xl font-bold leading-tight text-label-normal lg:mb-6 lg:text-left lg:text-4xl">
                잠깐! 프론트엔드 개발자,
                <br />
                적성에 맞을지 고민된다면?
              </h4>
              <div className="mb-6 w-full overflow-hidden lg:hidden">
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section12_img_m.png"
                  className="h-full w-full object-contain"
                  alt="-"
                />
              </div>
              <p className="mb-1 text-center text-sm leading-normal text-label-normal lg:text-left lg:text-lg">
                비전공자도 개발자로 취업할 수 있을까?
                <br />
                프론트엔드와 백엔드의 차이가 궁금하다면 주목!
                <br />
                멋쟁이사자처럼이 프론트엔드 개발자의 역할과 필수 역량,
                <br />
                그리고 2025년 채용 트렌드를 한눈에 정리했습니다.
                <br />
                개발 진로에 대한 고민이 있다면, 지금 바로 <br className="block lg:hidden" />
                멋쟁이사자처럼 <br className="hidden lg:block" />
                블로그에서 확인해 보세요!
              </p>
            </div>
            <div className="mt-10 lg:mt-16">
              <Link
                href="https://brunch.co.kr/@likelion/130"
                target="_blank"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:max-w-[395px] lg:gap-2.5 lg:py-5 lg:text-lg"
              >
                아티클 보러가기
                <img
                  className="h-4 w-4 lg:h-5 lg:w-5"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-design-5th/pre-orientation_icon.png"
                  alt="go link"
                />
              </Link>
            </div>
          </div>
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="hidden h-[388px] overflow-hidden rounded-lg lg:inline-block"
          >
            <img
              src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section12_img_pc.png"
              className="h-full w-full object-contain"
              alt="-"
            />
          </div>
        </div>
      </LazyLoadSection>

      {/* Section13_교육혜택 */}
      <LazyLoadSection
        height={1109}
        mobileHeight={1148}
        id="support"
        className="bg-gray-900 pb-[72px] pt-[88px] lg:pb-[100px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">교육 혜택</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-tight">
              비전공자도 끝까지 <br className="block lg:hidden" />
              따라올 수 있도록 <br className="hidden lg:block" />
              꼼꼼하고 <br className="block lg:hidden" />
              체계적으로 준비했어요
            </h4>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col gap-6 lg:flex-row lg:gap-10"
          >
            <ul className="mx-auto flex w-full flex-col gap-4 lg:flex-row lg:flex-wrap lg:gap-6">
              {benefitDetailList.map(benefit => (
                <li
                  className="flex w-full flex-col gap-4 overflow-hidden rounded-lg bg-trueGray-800 p-4 lg:w-[calc(50%-12px)] lg:flex-row lg:gap-6 lg:p-6"
                  key={benefit.title}
                >
                  <img
                    className="hidden h-[161px] w-[161px] object-cover lg:inline-block"
                    src={benefit.url}
                    alt="-"
                  />
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-row gap-2 lg:flex-col">
                      <p className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-[rgba(255,119,16,0.10)] text-xs font-bold text-yellow-450 lg:h-8 lg:w-8 lg:text-sm">
                        {benefit.step}
                      </p>
                      <h6 className="whitespace-pre-line text-lg font-bold leading-normal text-white lg:text-2xl">
                        {benefit.title}
                      </h6>
                    </div>
                    <div className="flex flex-row items-center justify-start gap-3">
                      <img
                        className="h-[78px] w-[78px] object-cover lg:hidden"
                        src={benefit.url}
                        alt="-"
                      />
                      <p className="flex-1 text-sm leading-normal text-white lg:text-base">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section14_취업혜택 */}
      <LazyLoadSection
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        height={1053}
        mobileHeight={873}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">취업 혜택</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl lg:leading-tight">
              든든한 취업 혜택으로
              <br />
              취업 준비까지 단번에 끝내세요
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              모든 수강생에게 개별 커리어 컨설팅을 제공하며
              <br />
              교육 수료 후에도 밀착 케어를 약속해요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col"
          >
            <div className="flex shrink-0 flex-row items-center justify-center gap-1 lg:items-start lg:justify-start lg:gap-3">
              {[
                { key: 'careerFirstList', label: '취업 방향성 \n탐색' },
                { key: 'careerSecondList', label: '취업 \n역량 강화' },
                { key: 'careerThirdList', label: '수료 후 \n지속 케어' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() =>
                    setCareerActiveTab(
                      key as 'careerFirstList' | 'careerSecondList' | 'careerThirdList',
                    )
                  }
                  className={`w-full whitespace-pre-line rounded-t-lg border-x border-t px-4 py-3 text-center text-base font-bold transition-all lg:whitespace-normal lg:px-8 lg:py-6 lg:text-left lg:text-2xl ${
                    careerActiveTab === key
                      ? 'border-[#FDFDFD] bg-[#FDFDFD] text-yellow-450'
                      : 'border-[#525252] bg-[#2E2E2E] text-label-alternative'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="w-full">
              {careerActiveTab === 'careerFirstList' && (
                <div className="flex flex-col gap-3 rounded-b-lg bg-[#FDFDFD] p-4 lg:flex-row lg:items-center lg:gap-11 lg:p-8">
                  <div className="w-full lg:w-[339px]">
                    <Image
                      src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section14_img1_${
                        isMobile ? 'm' : 'pc'
                      }.png`}
                      layout="fill"
                      objectFit="cover"
                      className="!relative w-full"
                      alt="-"
                    />
                  </div>
                  <ul className="flex w-full flex-col lg:w-auto lg:flex-1">
                    <li className="border-b pb-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        취업 준비 로드맵 특강
                      </p>
                    </li>
                    <li className="pt-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        개별 진로 탐색, 취업 로드맵 수립, 포트폴리오 작업 방향성 탐색
                      </p>
                    </li>
                  </ul>
                </div>
              )}
              {careerActiveTab === 'careerSecondList' && (
                <div className="flex flex-col gap-3 rounded-b-lg bg-[#FDFDFD] p-4 lg:flex-row lg:items-center lg:gap-11 lg:p-8">
                  <div className="w-full lg:w-[339px]">
                    <Image
                      src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section14_img2_${
                        isMobile ? 'm' : 'pc'
                      }.png`}
                      layout="fill"
                      objectFit="cover"
                      className="!relative w-full"
                      alt="-"
                    />
                  </div>
                  <ul className="flex w-full flex-col lg:w-auto lg:flex-1">
                    <li className="border-b pb-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        이력서/자기소개서 작성 방법 특강 진행
                      </p>
                    </li>
                    <li className="pt-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        합격하는 포트폴리오 작성법과 면접 클리닉
                      </p>
                    </li>
                  </ul>
                </div>
              )}
              {careerActiveTab === 'careerThirdList' && (
                <div className="flex flex-col gap-3 rounded-b-lg bg-[#FDFDFD] p-4 lg:flex-row lg:items-center lg:gap-11 lg:p-8">
                  <div className="w-full lg:w-[339px]">
                    <Image
                      src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section14_img3_${
                        isMobile ? 'm' : 'pc'
                      }.png`}
                      layout="fill"
                      objectFit="cover"
                      className="!relative w-full"
                      alt="-"
                    />
                  </div>
                  <ul className="flex w-full flex-col lg:w-auto lg:flex-1">
                    <li className="border-b pb-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        우수 수료생 대상 인턴십 기회 제공 및 채용 연계
                      </p>
                    </li>
                    <li className="border-b py-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        교육 과정 수료 이후에도 지속적인 구직활동 현황 체크
                      </p>
                    </li>
                    <li className="border-b py-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        스타트업 인사팀장 출신 코치의 1:1 상담 프로그램 지원
                      </p>
                    </li>
                    <li className="pt-2">
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section14} text-sm leading-normal text-label-normal lg:text-lg`}
                      >
                        수료 후 6개월간 이력서 및 포트폴리오 첨삭 제공
                      </p>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-10 text-center text-sm leading-normal text-label-alternative lg:mt-16 lg:text-lg">
              * 교육생 훈련 상황 및 회사 사정에 따라 <br className="block lg:hidden" />
              지원 내용이 변동 될 수 있습니다.
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section4_수강추천대상 */}
      <LazyLoadSection
        className="bg-strong pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        height={1030}
        mobileHeight={1009}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">수강 추천 대상</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-label-normal lg:text-4xl lg:leading-tight">
              나에게 최적화된 학습 방법으로
              <br />
              프론트엔드 개발자 취업을 끝내세요
            </h4>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col gap-6"
          >
            <li className="flex w-full flex-col items-stretch justify-center overflow-hidden rounded-lg lg:flex-row">
              <div className="w-full lg:w-2/5 lg:max-w-[500px]">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section4_img1_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-2 bg-[#FDFDFD] p-4 lg:w-auto lg:flex-1 lg:items-start lg:pl-[6.5%]">
                <h6 className="text-center text-lg font-bold text-label-normal lg:text-left lg:text-2xl">
                  기본기부터 탄탄하게 배우고 싶다면
                </h6>
                <p className="text-center text-sm leading-normal text-label-neutral lg:text-left lg:text-base">
                  정규 과정이 시작되기 전, <b className="text-label-normal">프리러닝 VOD</b>로{' '}
                  <br className="block lg:hidden" />
                  워밍업을 하고 <br className="hidden lg:block" />
                  <b className="text-label-normal">데일리 회고와 1:1 코드 리뷰</b>를 통해{' '}
                  <br className="block lg:hidden" />단 한 명의 낙오자가 발생되지 않도록{' '}
                  <br className="hidden lg:block" />
                  강사님과 <br className="block lg:hidden" />
                  멘토진이 기본기부터 탄탄하게 알려드릴게요.
                </p>
              </div>
            </li>
            <li className="flex w-full flex-col items-stretch justify-center overflow-hidden rounded-lg lg:flex-row">
              <div className="w-full lg:w-2/5 lg:max-w-[500px]">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section4_img2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-2 bg-[#FDFDFD] p-4 lg:w-auto lg:flex-1 lg:items-start lg:pl-[6.5%]">
                <h6 className="text-center text-lg font-bold text-label-normal lg:text-left lg:text-2xl">
                  취업 경쟁력을 높이고 싶다면
                </h6>
                <p className="text-center text-sm leading-normal text-label-neutral lg:text-left lg:text-base">
                  <b className="text-label-normal">실무 중심의 프로젝트</b>를 직접 설계 및 구현하는{' '}
                  <br className="block lg:hidden" />
                  과정을 포트폴리오에 <br className="hidden lg:block" />
                  담는 것은 물론, 나만의 <br className="block lg:hidden" />
                  취업 준비 로드맵을 그리고 <b className="text-label-normal">전담 커리어 코치진</b>
                  의 <br />
                  수료 후까지 지속되는 취업 케어를 받아보세요!
                </p>
              </div>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section15_강사진 */}
      <LazyLoadSection
        id="teacher"
        className="bg-trueGray-800 pb-[104px] pt-[88px] lg:py-[120px]"
        height={749}
        mobileHeight={926}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">강사진</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl lg:leading-tight">
              처음부터 끝까지 책임지는
              <br />
              프론트엔드 부트캠프 전문 강사진
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              환경이 달라지면 커리어가 달라지니까!
              <br />
              멋쟁이사자처럼이 13년간 쌓아온 노하우로 끝까지 책임져요
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex w-full flex-col rounded-lg bg-[#333333] p-4 lg:flex-row lg:p-6"
            >
              <div className="mb-3 flex w-full flex-row items-center justify-start gap-4 lg:mb-0 lg:mr-6 lg:w-[193px]">
                <div className="h-16 w-16 lg:h-[193px] lg:w-[193px]">
                  <Image
                    src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section15_img1_${
                      isMobile ? 'm' : 'pc'
                    }.png`}
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="-"
                  />
                </div>
                <h6 className="text-lg font-bold text-white lg:hidden">김데레사 강사님</h6>
              </div>
              <div className="w-full lg:w-auto lg:flex-1">
                <h6 className="hidden text-lg font-bold text-white lg:block">김데레사 강사님</h6>
                <ul className="flex flex-col gap-0.5">
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) EUID(이듬) 강사
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) NIA, K-MOOC 웹접근성 자문위원
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) 멀티캠퍼스 사외강사
                  </li>
                </ul>
                <ul className="mt-4 flex max-w-[270px] flex-row flex-wrap gap-2">
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #HTML
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #CSS
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #웹표준
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #웹접근성
                  </li>
                </ul>
              </div>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className="flex w-full flex-col rounded-lg bg-[#333333] p-4 lg:flex-row lg:p-6"
            >
              <div className="mb-3 flex w-full flex-row items-center justify-start gap-4 lg:mb-0.5 lg:mr-6 lg:w-[193px]">
                <div className="h-16 w-16 lg:h-[193px] lg:w-[193px]">
                  <Image
                    src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section15_img2_${
                      isMobile ? 'm' : 'pc'
                    }.png`}
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="-"
                  />
                </div>
                <h6 className="text-lg font-bold text-white lg:hidden">야무 강사님</h6>
              </div>
              <div className="w-full lg:w-auto lg:flex-1">
                <h6 className="hidden text-lg font-bold text-white lg:block">야무 강사님</h6>
                <ul className="flex flex-col gap-0.5">
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) EUID(이듬) 강사
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) NIA, K-MOOC 웹접근성 자문위원
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_section15} text-sm leading-normal text-white lg:text-base`}
                  >
                    현) Samsung, LG, SK, 신세계 등 기업 사외강사
                  </li>
                </ul>
                <ul className="mt-4 flex max-w-[270px] flex-row flex-wrap gap-2">
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #React
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #파이널 프로젝트
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #경력 개발자도 찾아듣는
                  </li>
                  <li className="max-w-fit rounded bg-[rgba(255,119,16,0.10)] px-3 py-[5.5px] text-sm font-bold text-yellow-450">
                    #야무진 강의
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section16_추가교육혜택 */}
      <LazyLoadSection
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        height={835}
        mobileHeight={1223}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">추가 교육 혜택</p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-4xl lg:leading-tight">
              열정만 갖고 있다면 충분해요
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
              멋쟁이사자처럼이 준비한 다양한 교육 혜택을 통해
              <br />
              오직 취업에만 집중할 수 있는 교육 환경을 제공해요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section16_mediaquery} flex flex-col flex-wrap gap-3 lg:flex-row lg:gap-6`}
          >
            {benefitSlideList.map(({ desc, title, imgUrlPc, imgUrlM }) => (
              <div
                className="flex flex-col justify-between gap-3 rounded-lg bg-trueGray-800 p-4 lg:h-[290px] lg:w-[calc(25%-18px)] lg:gap-6 lg:px-6 lg:py-5"
                key={title}
              >
                <div className="flex flex-col gap-1">
                  <h6 className="text-base font-bold text-white lg:text-lg">{title}</h6>
                  <p className="whitespace-pre-wrap text-sm leading-normal text-label-assistive lg:whitespace-normal lg:text-base">
                    {desc}
                  </p>
                </div>
                <div className="flex flex-row items-end justify-end">
                  <img
                    src={isMobile ? imgUrlM : imgUrlPc}
                    className="block h-[96px] w-auto object-contain lg:h-[120px]"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </LazyLoadSection>

      {/* Section17_사전설명회 */}
      {isAfterEarlySessionDate ? (
        <LazyLoadSection
          className="bg-[#FFF5EF] pb-24 pt-20 lg:pb-40 lg:pt-36"
          height={708}
          mobileHeight={694}
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-10 flex flex-col items-center justify-center lg:mb-16 lg:gap-4"
            >
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-4xl">
                사전설명회 다시보기
              </h4>
              <p className="hidden text-center text-sm leading-normal text-label-normal lg:block lg:text-lg">
                프론트엔드 개발자, 나와 잘 맞을까? 고민된다면,
                <br />
                멋쟁이사자차럼 프론트엔드 부트캠프 강사님과 함께한
                <br />
                사전설명회에서 그 궁금증을 모두 해결하세요.
              </p>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col"
            >
              <div
                className="h-auto overflow-hidden"
                style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                }}
              >
                <ReactPlayer
                  alt="멋쟁이사자처럼 사전설명회 LIVE"
                  url="https://youtube.com/live/P-bZZ5s-bWk"
                  controls={true}
                  width="100%"
                  height="100%"
                  muted
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
            </div>
            <p className="mt-6 text-center text-sm leading-normal text-label-normal lg:hidden lg:text-lg">
              프론트엔드 개발자, 나와 잘 맞을까? 고민된다면,
              <br />
              멋쟁이사자차럼 프론트엔드 부트캠프 강사님과 함께한
              <br />
              사전설명회에서 그 궁금증을 모두 해결하세요.
            </p>
          </div>
        </LazyLoadSection>
      ) : (
        <LazyLoadSection
          className="bg-[#FFF5EF] pb-24 pt-20 lg:py-40"
          height={708}
          mobileHeight={694}
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:flex lg:max-w-7xl lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="lg:w-1/2 lg:min-w-[400px] lg:max-w-[459px]"
            >
              <div className="flex w-full flex-col items-center justify-center lg:items-start">
                <p className="hidden text-sm font-bold text-yellow-450 lg:mb-2 lg:block lg:text-lg">
                  사전설명회 안내
                </p>
                <h4 className="mb-10 text-center text-2xl font-bold leading-tight text-label-normal lg:mb-6 lg:text-left lg:text-4xl">
                  프론트엔드 부트캠프에 대한
                  <br />
                  궁금증을 한 번에 해결하세요!
                </h4>
                <div className="mb-6 w-full overflow-hidden lg:hidden">
                  <img
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section17_img_re_m.png"
                    className="h-full w-full object-contain"
                    alt="*참고용 사전설명회 진행 모습"
                  />
                </div>
                <p className="mb-1 text-center text-sm leading-normal text-label-normal lg:text-left lg:text-lg">
                  6개월에 걸친 프론트엔드 부트캠프, <br className="block lg:hidden" />
                  유튜브 LIVE를 통해 <br className="hidden lg:block" />
                  어떤 교육이 진행될지 <br className="block lg:hidden" />
                  강사님께 직접 물어보세요!
                </p>
                <p className="text-center text-sm font-bold leading-normal text-label-normal lg:text-left lg:text-lg">
                  · 일정 : 2025년 4월 2일(수) 오후 7시
                  <br />· 장소 : YouTube 라이브 스트리밍
                </p>
              </div>
              <div className="mt-10 lg:mt-16">
                <Link
                  href="https://likelion2.typeform.com/to/BDGXS0qp"
                  target="_blank"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:max-w-[395px] lg:gap-2.5 lg:py-5 lg:text-lg"
                >
                  사전설명회 신청하기
                  <img
                    className="h-4 w-4 lg:h-5 lg:w-5"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-design-5th/pre-orientation_icon.png"
                    alt="사전설명회"
                  />
                </Link>
              </div>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="hidden h-[388px] overflow-hidden lg:inline-block"
            >
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-14th/section17_img_re_pc.png"
                className="h-full w-full object-contain"
                alt="*참고용 사전설명회 진행 모습"
              />
            </div>
          </div>
        </LazyLoadSection>
      )}

      {/* section18_지원및선발일정 */}
      <LazyLoadSection
        height={731}
        mobileHeight={756}
        id="condition"
        className="bg-[#FDFDFD] pb-[72px] pt-[88px] lg:pb-[100px] lg:pt-36"
      >
        <div className="mx-auto flex w-full max-w-xl flex-col px-4 lg:mx-auto lg:max-w-7xl lg:flex-row lg:flex-wrap lg:gap-6 lg:px-6">
          <div className="w-full lg:w-auto lg:flex-1">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-6 lg:mb-8"
            >
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-3xl">
                지원 및 선발 안내
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="rounded-lg bg-strong px-4 py-4 lg:px-6 lg:py-5"
            >
              {/* Timer */}
              {isAfterNewSessionDate ? (
                <>
                  {ApplyScheduleList2.map(edu => (
                    <dl
                      className={`${styles.section18_custom_dl} flex flex-row items-start gap-4 border-b py-2 first-of-type:pt-0 last-of-type:items-center last-of-type:border-0 last-of-type:pb-0 lg:py-3`}
                      key={edu.title}
                    >
                      <dt className="w-[61px] whitespace-pre-line text-sm font-bold leading-normal text-yellow-450 lg:w-[81px] lg:text-base">
                        {edu.title}
                      </dt>
                      <dd
                        className={`flex flex-1 flex-col whitespace-pre-line text-left text-sm leading-normal text-label-normal lg:text-base ${edu.contentList ? 'cursor-pointer' : ''}`}
                        onClick={() =>
                          edu.contentList &&
                          setOpenedTitle(openedTitle === edu.title ? null : edu.title)
                        }
                      >
                        <div className="flex flex-row items-center gap-4">
                          {edu.content}
                          {edu.contentList && (
                            <ChevronDownIcon
                              className={`${
                                openedTitle === edu.title ? 'rotate-180 transform' : ''
                              } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral lg:w-5`}
                            />
                          )}
                        </div>
                        {edu.contentList && openedTitle === edu.title && (
                          <ul className="mt-2 flex flex-col lg:mt-3">
                            {edu.contentList.map(item => (
                              <li key={item} className="border-t py-2 last-of-type:pb-0">
                                <p
                                  className={`${styles.text_dot} ${styles.text_dot_section18} text-sm leading-normal text-label-normal lg:text-base`}
                                >
                                  {item}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </dl>
                  ))}
                </>
              ) : (
                <>
                  {ApplyScheduleList1.map(edu => (
                    <dl
                      className={`${styles.section18_custom_dl} flex flex-row items-start gap-4 border-b py-2 first-of-type:pt-0 last-of-type:items-center last-of-type:border-0 last-of-type:pb-0 lg:py-3`}
                      key={edu.title}
                    >
                      <dt className="w-[61px] whitespace-pre-line text-sm font-bold leading-normal text-yellow-450 lg:w-[81px] lg:text-base">
                        {edu.title}
                      </dt>
                      <dd
                        className={`flex flex-1 flex-col whitespace-pre-line text-left text-sm leading-normal text-label-normal lg:text-base ${edu.contentList ? 'cursor-pointer' : ''}`}
                        onClick={() =>
                          edu.contentList &&
                          setOpenedTitle(openedTitle === edu.title ? null : edu.title)
                        }
                      >
                        <div className="flex flex-row items-center gap-4">
                          {edu.content}
                          {edu.contentList && (
                            <ChevronDownIcon
                              className={`${
                                openedTitle === edu.title ? 'rotate-180 transform' : ''
                              } ml-auto h-auto w-4 shrink-0 grow-0 text-label-neutral lg:w-5`}
                            />
                          )}
                        </div>
                        {edu.contentList && openedTitle === edu.title && (
                          <ul className="mt-2 flex flex-col lg:mt-3">
                            {edu.contentList.map(item => (
                              <li key={item} className="border-t py-2 last-of-type:pb-0">
                                <p
                                  className={`${styles.text_dot} ${styles.text_dot_section18} text-sm leading-normal text-label-normal lg:text-base`}
                                >
                                  {item}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </dl>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className="mt-20 w-full lg:mt-0 lg:w-auto lg:flex-1">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-6 lg:mb-8"
            >
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-3xl">
                교육 일정 안내
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="rounded-lg bg-strong px-4 py-4 lg:min-h-[419px] lg:px-6 lg:py-5"
            >
              {EduScheduleList.map(edu => (
                <li
                  className="flex flex-row items-center justify-center gap-4 border-b py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-8 lg:py-3"
                  key={edu.title}
                >
                  <h6 className="w-[61px] text-sm font-bold leading-normal text-yellow-450 lg:w-[81px] lg:text-base">
                    {edu.title}
                  </h6>
                  <p className="flex flex-1 flex-col text-sm text-label-normal lg:flex-row lg:gap-1.5 lg:text-base">
                    {edu.content}
                    {edu.caution && <span className="text-label-neutral">{edu.caution}</span>}
                  </p>
                </li>
              ))}
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* section19_자주묻는질문 */}
      <LazyLoadSection
        className="bg-white pb-[104px] pt-[88px] lg:pb-40 lg:pt-36"
        height={1231}
        mobileHeight={913}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <h4 className="mb-6 text-center text-xl font-bold text-label-normal lg:text-4xl">
            자주 묻는 질문
          </h4>
          <ul className="mx-auto mb-10 flex max-w-[324px] flex-wrap items-center justify-center gap-1.5 lg:mb-16 lg:max-w-none lg:flex-nowrap lg:gap-2">
            {bootCampFaqTabList.map(tabItem => {
              const isActive = tabItem.value === faqTab
              return (
                <li
                  className={`${
                    isActive
                      ? 'bg-yellow-450 text-white'
                      : 'border border-normal bg-white text-gray-400'
                  } cursor-pointer rounded-full px-5 py-1.5 text-xs font-bold lg:px-6 lg:py-2 lg:text-sm`}
                  onClick={() => onClickFaqTab(tabItem.value)}
                  key={tabItem.value}
                >
                  {tabItem.label}
                </li>
              )
            })}
          </ul>
          <ul className="mx-auto flex flex-col">
            {faqList.map(faq => (
              <li
                className={`${styles.faqItem} border-b border-[#E5E5E5] py-4 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-6`}
                key={faq.question}
              >
                <details className="group overflow-hidden">
                  <summary className="cursor-pointer list-none group-open:pb-1 lg:group-open:pb-2">
                    <div className="flex w-full items-center justify-between gap-4">
                      <div>
                        <p
                          className={`${styles.faq_item_title} text-sm font-semibold text-label-normal lg:text-lg`}
                        >
                          {faq.question}
                        </p>
                      </div>
                      <ChevronDownIcon className="w-4 shrink-0 text-gray-500 group-open:rotate-180 lg:w-6" />
                    </div>
                  </summary>
                  <div className="pl-5 lg:pl-[22px]">
                    <div className="whitespace-pre-wrap text-sm leading-normal text-gray-500 lg:text-base">
                      {faq.answer && (
                        <span className="max-w-full">
                          {faq.answer}
                          {faq.externalUrl1 && (
                            <>
                              {' '}
                              자세한 발급 과정은 아래 링크를 통해 확인 가능합니다.
                              <br />-{' '}
                              <Link href={faq.externalUrl1} target="_blank" className="underline">
                                {' '}
                                {faq.urlName1}
                              </Link>
                              {faq.secondAnswer}
                            </>
                          )}
                          {faq.externalUrl2 && (
                            <>
                              <br />-{' '}
                              <Link href={faq.externalUrl2} target="_blank" className="underline">
                                {' '}
                                {faq.urlName2}
                              </Link>
                            </>
                          )}
                        </span>
                      )}
                      {faq.answerList &&
                        faq.answerList.map(el => (
                          <p key={el} className="text-sm text-gray-500 lg:text-base">
                            {el}
                          </p>
                        ))}
                    </div>
                  </div>
                </details>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-md bg-[#FFF3EB] p-3 lg:mt-16 lg:flex-row lg:items-center lg:rounded-lg lg:p-10">
            <p className="flex-1 text-center text-sm font-semibold text-label-normal lg:text-left lg:text-lg lg:font-bold">
              더 궁금한 점이 있다면 <br className="block lg:hidden" />
              언제든지 교육 매니저에게 물어보세요!
            </p>
            <div className="flex w-full flex-row gap-1.5 lg:w-auto lg:gap-2.5">
              <Link
                href="https://likelion.channel.io/workflows/710054"
                target="_blank"
                className="flex h-9 flex-1 flex-row items-center justify-center gap-1.5 rounded bg-white px-3 text-sm font-bold text-yellow-450 lg:h-[50px] lg:w-[206px] lg:gap-2 lg:rounded-lg lg:text-lg"
              >
                1:1 문의하기
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/faq_icon_btn.png"
                  className="h-4 w-4 shrink-0 object-contain lg:h-[18px] lg:w-[18px]"
                  alt="1:1 문의하기"
                />
              </Link>
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-yellow-450 lg:h-[50px] lg:w-[50px] lg:rounded-lg"
                onClick={() =>
                  copyUrlShare('https://bootcamp.likelion.net/school/kdt-frontend-14th')
                }
              >
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-frontend-13th/faq_icon_share.png"
                  className="h-4 w-4 shrink-0 object-contain lg:h-6 lg:w-6"
                />
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>
    </div>
  )
}

export default FrontKlass14th
